import {
  CMRDamage,
  CMRVehicle,
  ContractorType,
  FranchiseEnum,
  OrderDetail
} from '@insurance/api/requests'
import { getEffectiveDateRange } from '@insurance/utils'
import { Card, Col, Divider, Form, Modal, Row } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CmrInsuranceProvider } from './CmrInsuranceContext'
import { CmrInsuranceControls } from './CmrInsuranceControls'
import { CustomerStep } from './CustomerStep'
import { Details } from './Details'
import { MainStep } from './MainStep'
import {
  formProps,
  getCmrValuesFromStorage,
  orderParentOrChildId,
  removeCmrValuesFromStorage,
  saveCmrValuesToStorage
} from './utils'
import { iniMixtContractorValues } from '../PersonsFields'
import { InformationStep } from './InformationStep'

type Props = {
  order?: OrderDetail
  isEditable?: boolean
}

const forms = [
  <MainStep key={0} />,
  <InformationStep key={1} />,
  <CustomerStep key={2} />
]

const {
  stepKey,
  priceKey,
  noDamagesProps,
  contractorProps,
  orderKey,
  franchiseProps,
  additionalExpensesProps,
  employeeErrorsProps,
  contractorHeadProps,
  contractorContactRoleProps,
  contractorActivityTypeProps,
  contractorTransportLicense,
  effectiveDateProps,
  modifyReasonProps,
  currencyAmountProps
} = formProps()

const initValues = {
  [stepKey.name]: 0,
  [priceKey.name]: undefined,
  [orderKey.name]: undefined,
  [contractorHeadProps.name]: undefined,
  [contractorContactRoleProps.name]: undefined,
  [contractorActivityTypeProps.name]: undefined,
  [contractorTransportLicense.name]: undefined,
  [effectiveDateProps.name]: undefined,
  [modifyReasonProps.name]: undefined,
  [noDamagesProps.name]: false,
  [franchiseProps.name]: FranchiseEnum.GENERAL,
  [additionalExpensesProps.name]: true,
  [employeeErrorsProps.name]: true,
  [currencyAmountProps.name]: 'EUR',
  ...iniMixtContractorValues,
  [contractorProps.name]: {
    content_type: ContractorType.LEGALENTITY,
    legal_entity: undefined
  }
}

export const CmrInsurance = ({ order, isEditable }: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [isReady, setIsReady] = useState(false)

  const isEditableMode = !!(isEditable || order?.parent)

  const handleReadyToSaveData = () => setIsReady(true)

  const saveOnChangeValue = () => {
    const values = form.getFieldsValue(true)
    saveCmrValuesToStorage(values, order?.id)
    return null
  }

  const formattedInitValues = useMemo(() => {
    const detailValues = order?.data

    if (!detailValues) return initValues

    const effectiveDateRange = getEffectiveDateRange(order)

    return {
      ...initValues,
      ...detailValues,
      ...(detailValues.valid_from && {
        valid_from: dayjs(detailValues.valid_from)
      }),
      ...(detailValues.contractor_activity_since && {
        contractor_activity_since: dayjs(
          String(detailValues.contractor_activity_since)
        )
      }),
      ...(detailValues.vehicles && {
        vehicles: detailValues.vehicles.map((vehicle: CMRVehicle) => ({
          ...vehicle,
          year: vehicle?.year ? dayjs(String(vehicle?.year)) : undefined
        }))
      }),
      ...(detailValues.last_3_years_damages && {
        last_3_years_damages: detailValues.last_3_years_damages.map(
          (damage: CMRDamage) => ({
            ...damage,
            date: damage?.date ? dayjs(damage?.date) : undefined
          })
        )
      }),
      [priceKey.name]: { amount: order.full_price, currency: order.currency },
      [noDamagesProps.name]: !detailValues.last_3_years_damages?.length,
      [stepKey.name]: order.stage || 0,
      [orderKey.name]:
        isEditableMode && orderParentOrChildId(order)
          ? order?.parent?.id || order?.id
          : order?.id,
      [effectiveDateProps.name]: effectiveDateRange.minDate
    }
  }, [order])

  useEffect(() => {
    const stored = getCmrValuesFromStorage(order?.id)
    form.setFieldsValue({ ...formattedInitValues, ...stored })
  }, [form, formattedInitValues, order?.id])

  useEffect(() => {
    const stored = getCmrValuesFromStorage(order?.id)
    if (!stored) return

    const modal = Modal.confirm({
      onCancel: () => {
        removeCmrValuesFromStorage(order?.id)
        setIsReady(false)
        form.setFieldsValue(formattedInitValues)
      },
      title: t('have_saved_data'),
      onOk: () => setIsReady(true)
    })

    return () => modal.destroy()
  }, [form, t, formattedInitValues, order?.id])

  return (
    <CmrInsuranceProvider
      isEditable={isEditableMode}
      {...(isEditableMode && { order })}
    >
      <Form
        layout="vertical"
        form={form}
        onValuesChange={handleReadyToSaveData}
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} lg={18}>
            <Form.Item shouldUpdate noStyle>
              {() => <Card>{forms[form.getFieldValue(stepKey.name)]}</Card>}
            </Form.Item>
          </Col>

          <Col xs={24} lg={6}>
            <Card>
              <Details />
            </Card>
          </Col>
        </Row>

        <Divider />

        <CmrInsuranceControls
          steps={forms.length}
          onReadyToSaveData={handleReadyToSaveData}
        />

        {isReady && (
          <Form.Item shouldUpdate noStyle>
            {saveOnChangeValue}
          </Form.Item>
        )}
      </Form>
    </CmrInsuranceProvider>
  )
}
